import type { LocationQuery } from 'vue-router'
import { getParamsNumbers, getValidateId } from '~/utils/getParamsPageResult'
import { validateAllNumbers } from '~/utils/validateQueryParams'
import { findCountryById, fetchLotterySelect, fetchRulesLotterySelect } from '~/utils/playLottery.js'
import { useResultStore } from '~/store/result'

type Id = string | string[] | null | undefined

export type ResultsEmptyStateStep = 'country' | 'lottery' | 'numbers'

interface ResultsParamsState {
	country: any | null
	lottery: any | null
	numbers: any | null
	amount: number
	isMultipleBets: boolean
	isLotteryAvailableForCountry: boolean
	hasSufficientParams: boolean
	emptyStateStep: ResultsEmptyStateStep
}

const getEmptyStateStep = ({
	country,
	lottery,
	isLotteryAvailableForCountry,
}: {
	country: any | null
	lottery: any | null
	isLotteryAvailableForCountry: boolean
}): ResultsEmptyStateStep => {
	if (country && lottery && isLotteryAvailableForCountry) {
		return 'lottery'
	}

	if (country) {
		return 'country'
	}

	return null as any
}

const getCountry = (id: Id) => {
	const countryId = getValidateId(id, () => null)
	if (!countryId) return null
	return findCountryById(countryId)
}

const getLottery = (id: Id) => {
	const lotteryId = getValidateId(id, () => null)
	if (!lotteryId) return null
	return fetchLotterySelect(lotteryId)
}

const getAmount = (value: unknown) => {
	const parsed = parseInt(String(value ?? ''), 10)
	return Number.isNaN(parsed) || parsed <= 0 ? 1 : parsed
}

const countSelected = (numbers: Array<number | string | null | undefined> | null | undefined) => {
	if (!Array.isArray(numbers)) return 0
	return numbers.filter((value) => value !== null && value !== undefined && value !== '').length
}

const hasValidMultipleBetsNumbers = (numbers: any, rules: any) => {
	if (!numbers || !rules) return false

	const firstSelected = countSelected(numbers.first)
	const minFirstSelected = rules.first_block?.min_select ?? rules.first_block?.total ?? 0

	if (!rules.last_block) {
		return firstSelected >= minFirstSelected && firstSelected >= (rules.min_count_active ?? minFirstSelected)
	}

	const lastSelected = countSelected(numbers.last)
	const minLastSelected = rules.last_block.min_select ?? rules.last_block.total ?? 0
	const totalSelected = firstSelected + lastSelected

	return (
		firstSelected >= minFirstSelected &&
		lastSelected >= minLastSelected &&
		totalSelected >= (rules.min_count_active ?? minFirstSelected + minLastSelected)
	)
}

const hasValidNumbers = (lottery: any, numbers: any, isMultipleBets: boolean) => {
	if (!lottery || !numbers) return false

	const rules = fetchRulesLotterySelect(lottery, isMultipleBets)
	if (!rules) return false

	if (isMultipleBets && typeof rules.min_count_active === 'number') {
		return hasValidMultipleBetsNumbers(numbers, rules)
	}

	return validateAllNumbers({ lottery, numbers })
}

/**
 * Parses and validates the results page query params into a structured state object.
 * Includes derived flags like `hasSufficientParams` and `emptyStateStep` to drive the UI.
 * @param query - Vue Router `LocationQuery` (defaults to empty).
 */
export const getResultsParamsState = (query: LocationQuery = {}): ResultsParamsState => {
	const country = getCountry(query.country_id as Id)
	const lottery = getLottery(query.lottery_id as Id)
	const isMultipleBets = query.is_multiple_bets === 'true'
	const amount = getAmount(query.amount)
	const numbers = lottery ? getParamsNumbers({ query }) : null
	const isLotteryAvailableForCountry = Boolean(country && lottery && country.lotteries?.includes(lottery.id))
	const hasSufficientParams = Boolean(
		country && lottery && numbers && isLotteryAvailableForCountry && hasValidNumbers(lottery, numbers, isMultipleBets),
	)

	return {
		country,
		lottery,
		numbers,
		amount,
		isMultipleBets,
		isLotteryAvailableForCountry,
		hasSufficientParams,
		emptyStateStep: getEmptyStateStep({ country, lottery, isLotteryAvailableForCountry }),
	}
}

/**
 * Reactive composable that merges the current route query params with any country/lottery
 * overrides held in the result store. Returns a computed `ResultsParamsState`.
 */
export const useResultsParams = () => {
	const route = useRoute()
	const resultStore = useResultStore()

	return computed(() => {
		const queryState = getResultsParamsState(route.query)
		const activeCountry = resultStore.country ?? queryState.country
		const activeLottery = resultStore.lottery ?? queryState.lottery
		const isLotteryAvailableForCountry = Boolean(
			activeCountry && activeLottery && activeCountry.lotteries?.includes(activeLottery.id),
		)

		return {
			...queryState,
			emptyStateStep: getEmptyStateStep({
				country: activeCountry,
				lottery: activeLottery,
				isLotteryAvailableForCountry,
			}),
		}
	})
}
